import React, { useEffect } from 'react';
import StyledProductCard from '../../styled/ProductCard';
import Link from '../../Router/Link';
import { CampaignSlot, Flag } from '../../styled/ProductCard/wrapper';
import { capper } from '../../utils/number';
import { useProductCardImages } from './imageResolver';
import { GridView } from '../../preload/pages/CategoryPage/Providers/ViewSettingsProvider';
import { TEnergyBadge, resolveEnergyBadge } from './utils';
import { CommunicationResolver } from './CommunicationResolver';
import { useColors } from './useColors';
import { useCardHover } from './useCardHover';
import { reviewListing } from '../Review';

export interface IImage {
  mediaType: MediaType;
  url: string;
}

export interface IProductCard extends IProduct {
  onClick?: () => void;
  images: IImage[];
  communicationSlots: TCommunicationSlots;
  horizontalProductCard?: boolean;
  productView?: GridView;
  showContextualImage?: boolean;
  imageLoading?: 'lazy' | 'eager';
}

const ProductCard = (
  {
    colorScale,
    description,
    groupId,
    moderArtikelId,
    name,
    onClick,
    price,
    brandImage,
    variantCount,
    energyClass,
    url,
    images,
    communicationSlots,
    horizontalProductCard,
    productView = 'grid-small',
    showContextualImage,
    imageLoading,
  }: IProductCard,
  ref: React.ForwardedRef<HTMLAnchorElement>,
) => {
  const [hover, handleMouseEnter, handleMouseLeave] = useCardHover();
  const { image, contextualImage } = useProductCardImages(productView, images);
  const colors = useColors(colorScale);

  useEffect(() => {
    reviewListing();
  }, []);

  const campaign = communicationSlots.campaign as TBanner | undefined;

  const { overflow, priceCommunication, priceDesign, primary, secondary } = communicationSlots;

  const gridSize = productView === 'grid-small' ? 'small' : 'large';

  return (
    <Link
      Component={StyledProductCard}
      href={url}
      onPostNavigate={onClick}
      campaignSlot={
        campaign && (
          <CampaignSlot className={productView === 'grid-small' ? 'push-down-ltMd' : undefined}>
            <Flag
              bg={campaign.backgroundColor}
              textColor={campaign.textColor}
              className={productView === 'grid-small' ? 'small' : 'large'}>
              {campaign.title}
            </Flag>
          </CampaignSlot>
        )
      }
      imageLoading={imageLoading}
      brandImage={brandImage}
      colors={colors}
      description={description}
      energyBadge={resolveEnergyBadge(energyClass as TEnergyBadge)}
      groupId={groupId}
      moderArtikelId={moderArtikelId}
      horizontalProductCard={!!horizontalProductCard}
      image={image}
      onMouseEnterImage={handleMouseEnter}
      onMouseLeaveImage={handleMouseLeave}
      overflowSlot={<CommunicationResolver slot={overflow} size={gridSize} />}
      price={price}
      priceCommunication={priceCommunication}
      priceDesign={priceDesign}
      primarySlot={<CommunicationResolver slot={primary} size={gridSize} />}
      ref={ref}
      secondaryImage={contextualImage}
      secondarySlot={<CommunicationResolver slot={secondary} size={gridSize} />}
      showSecondaryImage={(showContextualImage && !hover) || (!showContextualImage && hover)}
      title={name}
      variantCount={variantCount > 1 ? capper(variantCount, undefined, '+') : undefined}
    />
  );
};

export default React.forwardRef(ProductCard);
